import MapView from "@arcgis/core/views/MapView";
import esriConfig from "@arcgis/core/config";
import Basemap from "@arcgis/core/Basemap";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import { LayerType } from "../../types/layer-type";
import {
  arcgisBaseURL,
  checkStageTerritoryFileIntersection,
  clearSelectedGraphic,
  getLayers,
  initializeMap,
  polylineOff,
  polylineOn,
  removeAllTerritory,
  removeTerritoryObject,
  setConvertedFeatures,
  setFileLayerVisibility,
  setObjectVisibility,
  triggerGeometryOperation,
  zoomIn,
  zoomOut,
} from "../actions/map";
import {
  MapSection,
  masterUtilitiesTypes,
  setLayersVisible,
  setLoading,
  setMapComponentReady,
  startDrawingTools,
  stopDrawingTools,
  validArcgisLayerTypes,
} from "../reducers/map";
import createViewer, {
  applyHoverEffect,
  changeBaseMap,
  CreateHybridBaseMap,
  finishDrawingProcess,
  getGeometryType,
  getPopUpTemplate,
  getSymbolByGeometryType,
  startDrawingProcess,
  stopMeasuring,
  updateSnappingSources,
  waitForViewerReady,
} from "../../utils/helpers-for-2d-map";
import {
  InfrastructureTypeIds,
  LayersDataTypes,
} from "../../types/layers-data-types";
import { Middleware } from "redux";
import { RootState } from "../store";
import MapImageLayer from "@arcgis/core/layers/MapImageLayer";
import PopupTemplate from "@arcgis/core/PopupTemplate";
import {
  addDrawnGraphic,
  removeDrawnGraphic,
  removeGraphicFromConvertedGeometry,
  setBuildingTerritoryGeometry,
  setMainOperationFeature,
  setSecondaryOperationFeature,
  setSelectedFeature,
  setStageTerritoryGeometry,
  setVisibleEditAttributes,
  stages,
  territoryTypes,
  updateBuildingTerritoryFeature,
  updateConvertedGeometryFeature,
  updateStageTerritoryFeature,
} from "../reducers/utilities-journal";
import { ArcgisHybridLayer, Layer } from "../../interfaces/layer";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import Graphic from "@arcgis/core/Graphic";
import SketchViewModel from "@arcgis/core/widgets/Sketch/SketchViewModel";
import SimpleFillSymbol from "@arcgis/core/symbols/SimpleFillSymbol";
import {
  clearLastSelectedGraphic,
  DrawVertices,
  EditGraphics,
  EndCreateGraphic,
  handleDeleteKey,
  handleFeatureClickStyle,
  handleViewerClick,
} from "../../utils/master-map-helper";
import Polygon from "@arcgis/core/geometry/Polygon";
import { v4 as uuidv4 } from "uuid";
import {
  defaultPolygonSymbol,
  defaultPolylineSymbol,
  getMapLayerKey,
  isProtectedGraphic,
} from "../../utils/helpers-for-utilities";
import { StageGraphic, StageType } from "../../interfaces/utilities";
import * as geometryEngine from "@arcgis/core/geometry/geometryEngine";
import * as projection from "@arcgis/core/geometry/projection";
import GeoJSONLayer from "@arcgis/core/layers/GeoJSONLayer";

export const masterUtilitiesMiddleware = (
  section: MapSection,
): Middleware<{}, RootState> => {
  return (store) => {
    const DRAWN_GRAPHICS_LAYER_ID = "drawnGraphicsLayer";
    let viewer: MapView | null = null;
    let baseMap: Basemap;
    let drawnObjectsLayer: GraphicsLayer;
    let loadedObjectsLayer: GraphicsLayer;
    let verticesTempLayer: GraphicsLayer;
    let sketchViewModel: SketchViewModel;
    const loadedLayersMap: Map<string, GraphicsLayer> = new Map();
    const { dispatch } = store;

    function AddLayers(viewer: MapView) {
      const { layers, visibleLayersId } = store.getState().map;

      const baseMapLayer = layers.find(
        (l) =>
          l.dataType === LayersDataTypes.BaseMap &&
          visibleLayersId.includes(l.id),
      );
      if (baseMapLayer && baseMapLayer !== ArcgisHybridLayer) {
        changeBaseMap(baseMapLayer, viewer, getBaseMap);
      }

      layers
        .filter(
          (layer) =>
            masterUtilitiesTypes.includes(layer.dataType) &&
            (layer.type === LayerType.ArcgisFeatureServer ||
              layer.type === LayerType.ArcgisMapServer ||
              layer.type === LayerType.GeoJSON),
        )
        .forEach((layer) => {
          if (layer.dataType === LayersDataTypes.BaseMap) return;
          const alwaysVisibleLayer =
            layer.dataType === LayersDataTypes.OmanBorder;
          const visible =
            visibleLayersId.includes(layer.id) ||
            (layer.parentId !== undefined &&
              visibleLayersId.includes(layer.parentId)) ||
            alwaysVisibleLayer;

          const layerProperties = {
            title: layer.label,
            id: layer.name,
            url: layer.url,
            visible: visible,
            editingEnabled: layer.canEdit ?? false,
            definitionExpression: layer.style2D?.query?.replace(
              " and LandUse <> 3",
              "",
            ),
            outFields: ["*"],
          } as any;

          if (layer.style2D?.default) {
            layerProperties.renderer = layer.style2D?.default;
          }

          if (layer.type === LayerType.ArcgisFeatureServer) {
            const arcgisLayer = new FeatureLayer(layerProperties);

            let hoverStyle = layer.style2D?.hover;
            const isHover = layer.style2D?.label?.type === "hover";
            const mode = (hoverStyle ? "style" : "") + (isHover ? "popup" : "");

            if (mode) {
              if (isHover && layer.style2D?.label) {
                arcgisLayer.popupTemplate = new PopupTemplate(
                  getPopUpTemplate(layer.style2D?.label),
                );
              }
              applyHoverEffect(
                viewer,
                arcgisLayer,
                mode,
                hoverStyle,
                layer.style2D?.fieldName,
              );
            }

            viewer.map.add(arcgisLayer);
          }

          if (layer.type === LayerType.ArcgisMapServer) {
            const mapImageLayer = new MapImageLayer({
              title: layer.label,
              id: layer.name,
              url: layer.url,
              visible: visible,
            });
            if (layer.style2D?.subLayers) {
              mapImageLayer.load().then(() => {
                mapImageLayer.allSublayers.forEach((subLayer) => {
                  subLayer.visible = layer.style2D!.subLayers!.includes(
                    subLayer.id,
                  );
                });
              });
            }
            viewer.map.add(mapImageLayer);
          }

          if (layer.type === LayerType.GeoJSON) {
            const layerProperties = {
              title: layer.label,
              id: layer.name,
              url: layer.url,
              visible: visible,
              outFields: ["*"],
            } as any;
            if (layer.style2D?.default) {
              layerProperties.renderer = layer.style2D?.default;
            }
            const geoJSONLayer = new GeoJSONLayer(layerProperties);

            viewer.map.add(geoJSONLayer);
          }
        });
    }
    function getBaseMap(layer: Layer) {
      return layer === ArcgisHybridLayer ? baseMap : null;
    }

    function updateSnappingWrapper() {
      const ignoredLayers = [verticesTempLayer];

      updateSnappingSources(viewer, sketchViewModel, ignoredLayers);
    }

    function AddConvertedObjectOnMap() {
      const {
        stageTerritoryGeometry,
        buildingTerritoryGeometry,
        convertedGeometry,
        fileHidden,
        hiddenTerritories,
        drawnGraphics,
      } = store.getState().masterUtilities;
      const validLayerIds = new Set<string>();

      stages.forEach((stage) => {
        const stageGeometry = convertedGeometry[stage];
        if (!stageGeometry) return;
        Object.keys(stageGeometry).forEach((fileName) => {
          validLayerIds.add(getMapLayerKey(stage, fileName));
        });
      });

      const expectedKeys = new Set(
        stages.flatMap((stage) => {
          const geom = convertedGeometry[stage];
          return geom
            ? Object.keys(geom).map((fileName) =>
                getMapLayerKey(stage, fileName),
              )
            : [];
        }),
      );

      for (const [key, layer] of loadedLayersMap) {
        if (!expectedKeys.has(key)) {
          viewer!.map.remove(layer);
          loadedLayersMap.delete(key);
        }
      }

      stages.forEach((stage) => {
        const stageGeometry = convertedGeometry[stage];
        if (!stageGeometry) return;

        Object.entries(stageGeometry).forEach(([fileName, fileData]) => {
          const isHidden = fileHidden?.[stage]?.[fileName] ?? false;

          const graphics = fileData.graphics.map((item: any) => {
            const geom = getGeometryType(JSON.parse(item.geometry));

            const symbol = getSymbolByGeometryType(geom.type);

            const graphic: StageGraphic = new Graphic({
              geometry: geom,
              attributes: item.attributes,
              symbol,
            });
            graphic.stage = stage;

            return graphic;
          });

          const layerId = fileName;
          const mapKey = getMapLayerKey(stage, fileName);

          const existingLayer = loadedLayersMap.get(mapKey);
          if (existingLayer) {
            viewer!.map.remove(existingLayer);
            loadedLayersMap.delete(mapKey);
          }

          const newLayer = new GraphicsLayer({
            id: layerId,
            graphics,
            visible: !isHidden,
          });

          viewer!.map.add(newLayer);
          loadedLayersMap.set(mapKey, newLayer);
        });
      });

      territoryTypes.forEach((type) => {
        const geometries =
          type === "stage" ? stageTerritoryGeometry : buildingTerritoryGeometry;
        const isHidden = hiddenTerritories[type];

        Object.entries(geometries).forEach(([stageKey, graphic]) => {
          if (!graphic) return;

          if (!graphic.attributes || typeof graphic.attributes !== "object") {
            graphic.attributes = {};
          }

          let id = graphic.attributes.uuid;

          if (!id) {
            const generatedId = uuidv4();
            graphic.attributes.uuid = generatedId;
            id = generatedId;
          }

          const alreadyExists = drawnObjectsLayer.graphics.some((g) => {
            const gid = g.attributes?.uuid;
            return gid === id;
          });

          if (!alreadyExists) {
            let symbol = graphic.symbol;

            if (!symbol) {
              const type = graphic.geometry?.type;
              if (type === "polygon") {
                symbol = defaultPolygonSymbol;
              } else if (type === "polyline") {
                symbol = defaultPolylineSymbol;
              }
            }

            const newGraphic = new Graphic({
              geometry: graphic.geometry,
              attributes: graphic.attributes,
              symbol,
              visible: !isHidden[stageKey as StageType],
            });

            drawnObjectsLayer.add(newGraphic);
          }
        });
      });

      if (drawnGraphics.length > 0) {
        drawnGraphics.forEach((storedGraphic) => {
          const storedId = storedGraphic.attributes?.uuid;
          if (!storedId) return;

          const alreadyExistsOnMap = drawnObjectsLayer.graphics.some(
            (g) => g.attributes?.uuid === storedId,
          );
          if (alreadyExistsOnMap) return;

          drawnObjectsLayer.add(storedGraphic);
        });
      }

      updateSnappingWrapper();
    }

    function clearSelection() {
      clearLastSelectedGraphic([
        drawnObjectsLayer,
        ...Array.from(loadedLayersMap.values()),
      ]);
      dispatch(setSelectedFeature(null));
      if (store.getState().masterUtilities.visibleEditAttributes) {
        dispatch(setVisibleEditAttributes(false));
      }
    }

    const getSystemUuids = (): Set<string> => {
      const {
        convertedGeometry,
        stageTerritoryGeometry,
        buildingTerritoryGeometry,
      } = store.getState().masterUtilities;
      const systemUuids = new Set<string>();

      for (const stage of stages) {
        const stageGeometry = convertedGeometry[stage];
        if (!stageGeometry) continue;
        for (const layer of Object.values(stageGeometry)) {
          layer.graphics.forEach((g) => {
            if (g.attributes?.uuid) systemUuids.add(g.attributes.uuid);
          });
        }
      }

      Object.values(stageTerritoryGeometry).forEach((g) => {
        if (g?.attributes?.uuid) systemUuids.add(g.attributes.uuid);
      });

      Object.values(buildingTerritoryGeometry).forEach((g) => {
        if (g?.attributes?.uuid) systemUuids.add(g.attributes.uuid);
      });

      return systemUuids;
    };

    return (next) => async (action) => {
      if (initializeMap.match(action)) {
        if (
          action.payload.section === section &&
          action.payload.mode === "2d"
        ) {
          if (viewer === null) {
            await next(action);
            baseMap = CreateHybridBaseMap();
            viewer = createViewer(
              store,
              AddLayers,
              `${section}MapContainer`,
              baseMap,
            );

            drawnObjectsLayer = new GraphicsLayer({
              id: DRAWN_GRAPHICS_LAYER_ID,
            });
            loadedObjectsLayer = new GraphicsLayer();
            verticesTempLayer = new GraphicsLayer();

            viewer.map.add(drawnObjectsLayer);
            viewer.map.add(loadedObjectsLayer);
            viewer.map.add(verticesTempLayer);

            sketchViewModel = new SketchViewModel({
              updateOnGraphicClick: false,
              defaultUpdateOptions: {
                tool: "reshape",
                toggleToolOnClick: false,
                reshapeOptions: {
                  shapeOperation: "none",
                },
              },
              layer: drawnObjectsLayer,
              view: viewer,
              snappingOptions: {
                enabled: true,
                selfEnabled: false,
                featureSources: [],
              },
              polygonSymbol: new SimpleFillSymbol({
                color: "#FAFAFA33",
                outline: {
                  color: "#FFFFFF",
                  width: 3,
                },
              }),
            });

            sketchViewModel.on("update", (event) => {
              const {
                selectedFeature,
                stageTerritoryGeometry,
                buildingTerritoryGeometry,
                selectedRow,
                convertedGeometry,
              } = store.getState().masterUtilities;

              if (
                isProtectedGraphic(
                  selectedFeature!,
                  stageTerritoryGeometry,
                  buildingTerritoryGeometry,
                  selectedRow!,
                  convertedGeometry,
                )
              ) {
                sketchViewModel.cancel();
                return;
              }

              if (event.toolEventInfo) {
                EditGraphics(event.graphics, dispatch, undefined, "uuid");
              } else {
                if (event.state === "start" || event.state === "complete")
                  event.graphics
                    .filter(
                      (graphic) =>
                        graphic.layer?.id === DRAWN_GRAPHICS_LAYER_ID,
                    )
                    .forEach((graphic) => {
                      DrawVertices(
                        graphic.geometry as Polygon,
                        graphic.attributes.uuid,
                        event.state === "start",
                        verticesTempLayer,
                      );
                    });
              }

              viewer?.map.reorder(
                verticesTempLayer,
                viewer.map.layers.length - 1,
              );
            });
            sketchViewModel.on("create", (event) => {
              if (event.state === "cancel" && !event.graphic?.attributes) {
                return;
              }

              if (event.state === "complete") {
                const uuid = uuidv4();
                event.graphic.attributes = {
                  ...(event.graphic.attributes ?? {}),
                  uuid,
                };

                const systemIds = getSystemUuids();

                if (!systemIds.has(uuid)) {
                  DrawVertices(
                    event.graphic.geometry as Polygon,
                    uuid,
                    false,
                    verticesTempLayer,
                  );
                  dispatch(addDrawnGraphic(event.graphic));
                }

                handleFeatureClickStyle(
                  event.graphic,
                  store,
                  drawnObjectsLayer,
                  viewer,
                  sketchViewModel,
                );

                dispatch(
                  setSelectedFeature({
                    layerName: event.graphic.layer.id,
                    attributes: event.graphic.attributes,
                    graphic: event.graphic,
                    isArea: event.graphic.geometry?.type === "polygon",
                  }),
                );
              }
            });

            viewer!.on("click", async (event) => {
              const response = await viewer!.hitTest(event);

              const clickedGraphicResult = response.results[0];

              if (
                clickedGraphicResult &&
                "graphic" in clickedGraphicResult &&
                clickedGraphicResult.graphic.layer === verticesTempLayer
              ) {
                return;
              }

              const visibleEditAttributesModal =
                store.getState().masterUtilities.visibleEditAttributes;

              handleViewerClick(
                event,
                store,
                sketchViewModel,
                viewer,
                [drawnObjectsLayer, ...Array.from(loadedLayersMap.values())],
                drawnObjectsLayer,
              );

              if (event.native?.button !== 0) return;

              if (clickedGraphicResult && "graphic" in clickedGraphicResult) {
                const graphic = clickedGraphicResult.graphic as StageGraphic;
                const isArea = graphic.geometry?.type === "polygon";
                const stage = graphic.stage;
                const isGraphicsLayer = graphic.layer instanceof GraphicsLayer;

                if (!isGraphicsLayer) {
                  const layerId = graphic.layer.id;
                  const layers = store
                    .getState()
                    .map.layers.filter((l) => l.name === layerId);
                  const allowedTypes = InfrastructureTypeIds.filter(
                    (id) => id !== LayersDataTypes.LogisticInfrastructure,
                  );
                  if (!layers.some((l) => allowedTypes.includes(l.dataType)))
                    return;
                }

                const layerName = isGraphicsLayer
                  ? graphic.layer.id
                  : graphic.layer.title;

                dispatch(
                  setSelectedFeature({
                    layerName,
                    attributes: graphic.attributes,
                    graphic,
                    isArea,
                    stage,
                  }),
                );

                if (
                  clickedGraphicResult.graphic.layer.id ===
                  DRAWN_GRAPHICS_LAYER_ID
                ) {
                  if (visibleEditAttributesModal) {
                    dispatch(setVisibleEditAttributes(false));
                  }
                } else {
                  dispatch(setVisibleEditAttributes(true));
                }
              } else {
                clearSelection();
              }
            });

            viewer.container.addEventListener(
              "keydown",
              (event: KeyboardEvent) => {
                const {
                  selectedFeature,
                  stageTerritoryGeometry,
                  buildingTerritoryGeometry,
                  selectedRow,
                  convertedGeometry,
                } = store.getState().masterUtilities;

                if (event.key === "Escape") {
                  clearSelection();
                  return;
                }

                if (
                  isProtectedGraphic(
                    selectedFeature!,
                    stageTerritoryGeometry,
                    buildingTerritoryGeometry,
                    selectedRow!,
                    convertedGeometry,
                  )
                )
                  return;
                const { drawingToolsIsActive } = store.getState().map;

                if (drawingToolsIsActive) {
                  const deletedGraphic = handleDeleteKey(
                    event,
                    store,
                    [
                      drawnObjectsLayer,
                      ...Array.from(loadedLayersMap.values()),
                    ],
                    verticesTempLayer,
                    [],
                    () => {},
                    undefined,
                    true,
                    sketchViewModel,
                  );

                  if (deletedGraphic) {
                    if (
                      "graphic" in deletedGraphic &&
                      "layer" in deletedGraphic
                    ) {
                      const { graphic, layer } = deletedGraphic;

                      if (layer.id !== DRAWN_GRAPHICS_LAYER_ID) {
                        const stage = (graphic as StageGraphic).stage;
                        const objectId = graphic.attributes.uuid;
                        dispatch(
                          removeGraphicFromConvertedGeometry({
                            objectId,
                            stage,
                          }),
                        );
                      } else {
                        dispatch(removeDrawnGraphic(graphic));
                      }
                    }
                  }

                  dispatch(setSelectedFeature(null));
                }
              },
            );

            viewer.container.addEventListener(
              "keydown",
              (event: KeyboardEvent) => {
                if (event.ctrlKey && event.code === "KeyZ") {
                  event.preventDefault();
                  event.stopPropagation();
                  sketchViewModel.undo();
                  return;
                }
              },
              { capture: true },
            );

            AddConvertedObjectOnMap();
            await waitForViewerReady(viewer);
            dispatch(setMapComponentReady(section));
            updateSnappingWrapper();
          }
          return;
        } else if (viewer !== null) {
          viewer.map?.removeAll();
          viewer?.destroy();
          viewer = null;
          dispatch(setMapComponentReady(MapSection.none));
        }
      }
      if (viewer === null || store.getState().map.section !== section) {
        return next(action);
      }
      if (setLayersVisible.match(action)) {
        next(action);
        const { layers, visibleLayersId } = store.getState().map;
        layers
          .filter((layer) => masterUtilitiesTypes.includes(layer.dataType))
          .forEach((layer) => {
            if (layer.dataType === LayersDataTypes.BaseMap) {
              if (visibleLayersId.includes(layer.id))
                changeBaseMap(layer, viewer, getBaseMap);
            } else if (validArcgisLayerTypes.includes(layer.type)) {
              let mapLayers = viewer!.map.layers.filter(
                (l) => l.id === layer.name,
              );
              if (mapLayers) {
                const alwaysVisibleLayer =
                  layer.dataType === LayersDataTypes.OmanBorder;
                const visible =
                  visibleLayersId.includes(layer.id) ||
                  (!!layer.parentId &&
                    visibleLayersId.includes(layer.parentId)) ||
                  alwaysVisibleLayer;
                Promise.all(
                  mapLayers.map((mapLayer) => {
                    mapLayer.visible = visible;
                    return mapLayer.load();
                  }),
                ).finally(() => {
                  dispatch(setLoading({ id: layer.id, loading: false }));
                });
              }
            }
          });
        return;
      }

      if (zoomIn.match(action)) {
        viewer.zoom += 1;
      }
      if (zoomOut.match(action)) {
        viewer.zoom -= 1;
      }

      if (setConvertedFeatures.match(action)) {
        AddConvertedObjectOnMap();

        const { selectedFeature } = store.getState().masterUtilities;

        if (selectedFeature) {
          const { layerName, attributes, stage } = selectedFeature;

          if (!stage) return;

          const selectedFeatureId = attributes.uuid;
          const mapKey = getMapLayerKey(stage, layerName);
          const layer = loadedLayersMap.get(mapKey);

          const graphic = layer?.graphics.find(
            (graphic) => graphic.attributes.uuid === selectedFeatureId,
          );

          if (!graphic) return;

          handleFeatureClickStyle(
            graphic,
            store,
            [...Array.from(loadedLayersMap.values())],
            viewer,
            sketchViewModel,
          );
        }
      }

      if (startDrawingTools.match(action)) {
        const systemUuids = getSystemUuids();

        drawnObjectsLayer.graphics.forEach((graphic) => {
          const uuid = graphic.attributes?.uuid;

          if (uuid && !systemUuids.has(uuid)) {
            DrawVertices(
              graphic.geometry as Polygon,
              uuid,
              false,
              verticesTempLayer,
            );
          }
        });
      }

      if (stopDrawingTools.match(action)) {
        if (
          sketchViewModel.state === "active" &&
          sketchViewModel.createGraphic
        ) {
          EndCreateGraphic(sketchViewModel);
        } else {
          sketchViewModel.complete();
        }
        const uuids = drawnObjectsLayer.graphics
          .map((graphic) => graphic.attributes?.uuid)
          .filter(Boolean);

        verticesTempLayer.removeMany(
          verticesTempLayer.graphics
            .filter((g) => uuids.includes(g.attributes.uuid))
            .toArray(),
        );
      }

      if (setObjectVisibility.match(action)) {
        const { objectId, visible } = action.payload;

        const targetGraphic = drawnObjectsLayer.graphics.find((g) => {
          const graphicId = g.attributes?.uuid;
          return graphicId === objectId;
        });

        if (targetGraphic) {
          targetGraphic.visible = visible;
        }

        const foundVertices = verticesTempLayer.graphics.filter((g) => {
          return g.attributes.uuid === objectId;
        });

        foundVertices.forEach((vertex) => {
          vertex.visible = visible;
        });

        return;
      }

      if (removeAllTerritory.match(action)) {
        const { stage } = action.payload;
        const { stageTerritoryGeometry, buildingTerritoryGeometry } =
          store.getState().masterUtilities;

        const territoriesToRemove = [
          stageTerritoryGeometry[stage],
          buildingTerritoryGeometry[stage],
        ].filter((geometry): geometry is Graphic => !!geometry);

        for (const territory of territoriesToRemove) {
          const uuid = territory.attributes?.uuid;

          if (uuid) {
            const mainGraphic = drawnObjectsLayer.graphics.find(
              (graphic) => graphic.attributes?.uuid === uuid,
            );
            if (mainGraphic) {
              drawnObjectsLayer.remove(mainGraphic);
            }

            const vertexGraphics = verticesTempLayer.graphics.filter(
              (vertex) => vertex.attributes.uuid === uuid,
            );
            verticesTempLayer.removeMany(vertexGraphics.toArray());
          }
        }

        store.dispatch(
          setStageTerritoryGeometry({ stageType: stage, geometry: null }),
        );
        store.dispatch(
          setBuildingTerritoryGeometry({ stageType: stage, geometry: null }),
        );

        return;
      }

      if (removeTerritoryObject.match(action)) {
        const { objectId } = action.payload;

        const targetGraphic = drawnObjectsLayer.graphics.find((g) => {
          const id = g.attributes?.uuid;
          return id === objectId;
        });

        if (targetGraphic) {
          drawnObjectsLayer.remove(targetGraphic);
        }

        const vertices = verticesTempLayer.graphics.filter(
          (g) => g.attributes.uuid === objectId,
        );
        verticesTempLayer.removeMany(vertices.toArray());

        return;
      }

      if (triggerGeometryOperation.match(action)) {
        const mode = action.payload;
        const {
          stageTerritoryGeometry,
          buildingTerritoryGeometry,
          mainOperationFeature,
          secondaryOperationFeature,
        } = store.getState().masterUtilities;

        const mainGeom = mainOperationFeature?.graphic.geometry;
        const secondGeom = secondaryOperationFeature?.graphic.geometry;

        if (!mainGeom || !secondGeom) return;

        let resultGeom;

        if (mode === "merge") {
          resultGeom = geometryEngine.union([mainGeom, secondGeom]);
        } else if (mode === "subtract") {
          resultGeom = geometryEngine.difference(mainGeom, secondGeom);
        }

        if (!resultGeom) return;

        const cleanGeom = Array.isArray(resultGeom)
          ? resultGeom[0]
          : resultGeom;

        const { stage: featureStage } = mainOperationFeature;

        const resultGraphic = new Graphic({
          geometry: cleanGeom,
          attributes: mainOperationFeature.graphic.attributes,
          symbol: mainOperationFeature.graphic.symbol,
        });

        if (featureStage) {
          (resultGraphic as StageGraphic).stage = featureStage;
        }

        const updatedFeature = {
          ...mainOperationFeature,
          graphic: resultGraphic,
        };

        const uuidMain = mainOperationFeature.attributes?.uuid;
        const uuidSecond = secondaryOperationFeature.attributes?.uuid;

        if (mainOperationFeature.layerName !== DRAWN_GRAPHICS_LAYER_ID) {
          dispatch(
            updateConvertedGeometryFeature({
              stage: mainOperationFeature.stage!,
              feature: updatedFeature,
            }),
          );
        } else if (
          Object.values(stageTerritoryGeometry).some(
            (g) => g?.attributes?.uuid === uuidMain,
          )
        ) {
          const stage = stages.find(
            (s) => stageTerritoryGeometry[s]?.attributes?.uuid === uuidMain,
          );
          if (stage) {
            dispatch(
              updateStageTerritoryFeature({ stage, feature: updatedFeature }),
            );
          }
        } else {
          const stage = stages.find(
            (s) => buildingTerritoryGeometry[s]?.attributes?.uuid === uuidMain,
          );
          if (stage) {
            dispatch(
              updateBuildingTerritoryFeature({
                stage,
                feature: updatedFeature,
              }),
            );
          }
        }

        const layerMain =
          mainOperationFeature.layerName === DRAWN_GRAPHICS_LAYER_ID ||
          !mainOperationFeature.stage
            ? drawnObjectsLayer
            : loadedLayersMap.get(
                getMapLayerKey(
                  mainOperationFeature.stage,
                  mainOperationFeature.layerName!,
                ),
              );
        const layerSecond =
          secondaryOperationFeature.layerName === DRAWN_GRAPHICS_LAYER_ID ||
          !secondaryOperationFeature?.stage
            ? drawnObjectsLayer
            : loadedLayersMap.get(
                getMapLayerKey(
                  secondaryOperationFeature.stage,
                  secondaryOperationFeature.layerName!,
                ),
              );

        const mainGraphic = layerMain?.graphics.find(
          (g) => g.attributes?.uuid === uuidMain,
        );
        const secondGraphic = layerSecond?.graphics.find(
          (g) => g.attributes?.uuid === uuidSecond,
        );

        if (mainGraphic) layerMain?.remove(mainGraphic);
        if (secondGraphic) layerSecond?.remove(secondGraphic);

        layerMain?.add(resultGraphic);
        if (secondGraphic) layerSecond?.add(secondGraphic);

        const isDrawnLayer =
          mainOperationFeature.layerName === DRAWN_GRAPHICS_LAYER_ID;

        if (isDrawnLayer) {
          const oldVertices = verticesTempLayer.graphics.filter(
            (v) => v.attributes.uuid === uuidMain,
          );
          verticesTempLayer.removeMany(oldVertices.toArray());
          DrawVertices(
            resultGraphic.geometry as Polygon,
            uuidMain,
            false,
            verticesTempLayer,
          );
        }

        viewer.map.reorder(verticesTempLayer, viewer.map.layers.length - 1);
        viewer.map.reorder(drawnObjectsLayer, viewer.map.layers.length - 2);

        dispatch(setMainOperationFeature(null));
        dispatch(setSecondaryOperationFeature(null));
      }

      if (setFileLayerVisibility.match(action)) {
        const { fileName, visible, stage } = action.payload;
        const mapKey = getMapLayerKey(stage, fileName);
        const layer = loadedLayersMap.get(mapKey);
        if (!layer) return;

        layer.visible = visible;

        const uuids = layer.graphics.toArray().map((g) => g.attributes?.uuid);

        verticesTempLayer.graphics.forEach((v) => {
          if (uuids.includes(v.attributes.uuid)) v.visible = visible;
        });

        return;
      }

      if (checkStageTerritoryFileIntersection.match(action)) {
        const { graphic, stage, resolve } = action.payload;
        const wkidSet = new Set<number>();

        for (const layer of loadedLayersMap.values()) {
          for (const graphic of layer.graphics) {
            const isSameStage = (graphic as StageGraphic).stage === stage;
            const sr = graphic.geometry?.spatialReference;

            if (isSameStage && sr?.wkid) {
              wkidSet.add(sr.wkid);
            }
          }
        }

        await projection.load();

        for (const wkid of wkidSet) {
          const projected = projection.project(graphic.geometry, { wkid });
          const geometries = Array.isArray(projected) ? projected : [projected];
          for (const layer of loadedLayersMap.values()) {
            const intersects = layer.graphics.some((graphic) => {
              const sameStage = (graphic as StageGraphic).stage === stage;
              const sameWkid = graphic.geometry.spatialReference.wkid === wkid;

              return (
                sameStage &&
                sameWkid &&
                graphic.geometry &&
                geometries.some((p) =>
                  geometryEngine.intersects(p, graphic.geometry),
                )
              );
            });

            if (intersects) {
              resolve(true);
            }
          }
        }

        resolve(false);
        return;
      }

      if (clearSelectedGraphic.match(action)) {
        clearSelection();
      }

      if (polylineOff.match(action)) {
        finishDrawingProcess(viewer);
        stopMeasuring(viewer);
      }
      if (polylineOn.match(action)) {
        startDrawingProcess(viewer);
      }

      if (getLayers.fulfilled.match(action)) {
        next(action);
        let token = store.getState().map.arcgisToken;
        if (token) {
          esriConfig.request.interceptors?.push({
            before: function (params) {
              if (!params.url.startsWith(arcgisBaseURL)) return;
              params.requestOptions.query = params.requestOptions.query || {};
              params.requestOptions.query.token = token!.token;
            },
          });
        }
        AddLayers(viewer);
        return;
      }

      next(action);
    };
  };
};
